// widget/src/main.tsx
// Bootstrap entry for the embeddable widget bundle.
//
// Runs immediately when widget.js loads on the host page. Reads config from
// the script tag's data-* attributes, fetches the client config from our
// backend, attaches a Shadow DOM, injects our compiled CSS + brand colors,
// and renders the real SearchWidget inside.
//
// `process.env.*` references in shared codebase files (SearchWidget reads
// NEXT_PUBLIC_CHAT_BACKEND, lib/clients/index.ts reads NEXT_PUBLIC_CLIENT_ID)
// are replaced at build time by Vite's `define` rules — see vite.config.ts.

import { createRoot } from 'react-dom/client';
import { ClientProvider } from '@/lib/clients/ClientContext';
import type { SerializedClientConfig } from '@/lib/clients/serialize';
import { resolveFont, type ResolvedFont } from '@/lib/clients/fonts';
import SearchWidget from '@/components/search/SearchWidget';
// Pre-compiled Tailwind output. Built by `npm run build:widget:css` (chained
// before vite build). The CLI properly resolves our @source directives and
// scans components/ + lib/ for class names — the Vite plugin doesn't.
import widgetCss from './widget.compiled.css?inline';

interface BootConfig {
  storeId: string;
  customerId: string | null;
  locale: string;
  /** Origin to call for our APIs. Derived from where widget.js was loaded from. */
  apiOrigin: string;
}

/** Read static configuration from the script tag's data-* attributes. */
function readBootConfig(scriptTag: HTMLScriptElement | null): BootConfig {
  return {
    storeId: scriptTag?.dataset.storeId || 'parousiasi',
    customerId:
      scriptTag?.dataset.customerId ||
      (typeof window !== 'undefined' &&
        (window as unknown as { AcaSearch?: { customerId?: string } }).AcaSearch?.customerId) ||
      null,
    locale: scriptTag?.dataset.locale || document.documentElement.lang || 'el',
    apiOrigin: deriveApiOrigin(scriptTag),
  };
}

/** Derive the API base URL from where our widget.js script was loaded from. */
function deriveApiOrigin(scriptTag: HTMLScriptElement | null): string {
  if (scriptTag?.src) {
    try {
      return new URL(scriptTag.src).origin;
    } catch {
      // fall through
    }
  }
  // Fallback: same origin as the host page (works for local dev)
  return window.location.origin;
}

/** Find this widget's <script> tag. document.currentScript works during initial
 *  script execution; we capture it eagerly at module load to avoid losing it. */
const ownScriptTag: HTMLScriptElement | null =
  document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : (() => {
        const all = document.querySelectorAll<HTMLScriptElement>('script[data-store-id]');
        return all[all.length - 1] ?? null;
      })();

/**
 * Build @font-face CSS for the resolved font config.
 *
 * The bundled widget.css already declares @font-face for Gotham Greek (the
 * default), but Chrome doesn't reliably apply Shadow-DOM-scoped @font-face
 * rules — so we always inject into the host document's <head> too. For
 * non-default fonts (when a client supplies their own brand font) this is the
 * only path: the build-time CSS only knows about the default.
 *
 * `originBase` is the URL where /widget.js was served from. We prepend it to
 * each file path so the fonts load whether the bundle is same-origin (dev) or
 * cross-origin (embedded on a third-party site).
 */
function buildFontFaceCss(font: ResolvedFont, originBase: string): string {
  return font.files
    .map(
      (f) => `
@font-face {
  font-family: "${font.family}";
  src: url("${originBase}/fonts/widget/${f.file}") format("woff2");
  font-weight: ${f.weight};
  font-style: ${f.style};${f.unicodeRange ? `\n  unicode-range: ${f.unicodeRange};` : ''}${font.sizeAdjust ? `\n  size-adjust: ${font.sizeAdjust};` : ''}
  font-display: swap;
}`,
    )
    .join('\n');
}

/**
 * Inject the resolved font's @font-face rules into the host document's <head>
 * (light DOM). Idempotent — keyed by font family so repeated calls for the
 * same font are no-ops, and a different font replaces the previous rules.
 */
function injectGlobalFontFaces(origin: string, font: ResolvedFont): void {
  const styleId = `aca-search-fonts-${font.key}`;
  // If we already injected this exact font, nothing to do.
  if (document.getElementById(styleId)) return;
  // Remove any previously-injected font block so a different font cleanly replaces it.
  document
    .querySelectorAll('style[data-aca-search-fonts]')
    .forEach((el) => el.parentNode?.removeChild(el));
  const style = document.createElement('style');
  style.id = styleId;
  style.setAttribute('data-aca-search-fonts', font.key);
  style.textContent = buildFontFaceCss(font, origin);
  document.head.appendChild(style);
}

/** Default font used during the brief window before the API config arrives
 * (the registry default — Commissioner since 2026-10-01). */
const DEFAULT_FONT: ResolvedFont = resolveFont(undefined);

/** Attach a shadow root to the container, inject Tailwind CSS, return the inner mount point. */
function createIsolatedRoot(container: HTMLElement, config: SerializedClientConfig): HTMLElement {
  const shadow = container.attachShadow({ mode: 'open' });

  // Inject the compiled Tailwind + brand utilities as a stylesheet inside the shadow root.
  const styleEl = document.createElement('style');
  styleEl.textContent = widgetCss;
  shadow.appendChild(styleEl);

  // Inner mount element. CSS variables for branding go on this element so all
  // descendants inherit them.
  const inner = document.createElement('div');
  inner.id = 'aca-search-root';
  applyBrandColors(inner, config);
  shadow.appendChild(inner);

  return inner;
}

/** Set per-client CSS variables on the widget root element. */
function applyBrandColors(el: HTMLElement, config: SerializedClientConfig): void {
  const ui = config.ui;
  el.style.setProperty('--color-brand', ui.brandColor);
  el.style.setProperty('--color-brand-hover', ui.brandColorHover ?? ui.brandColor);
  // Optional per-client color overrides (now passed through by serializeConfig).
  if (ui.scrollbarColor) el.style.setProperty('--color-scrollbar', ui.scrollbarColor);
  if (ui.brandTextColor) el.style.setProperty('--color-brand-text', ui.brandTextColor);
  if (ui.highlightColor) el.style.setProperty('--color-highlight', ui.highlightColor);
  if (ui.productTitleColor) el.style.setProperty('--color-product-title', ui.productTitleColor);
}

/**
 * Set per-client font CSS variables on the widget root element. The widget's
 * Tailwind classes (`font-gotham`) and Tailwind v4 preflight both resolve to
 * `--font-gotham-greek` / `--default-font-family`; we point those at whichever
 * font the client picked.
 */
function applyFontVariables(el: HTMLElement, font: ResolvedFont): void {
  const stack = `"${font.family}", ${font.fallbackStack}`;
  el.style.setProperty(font.cssVariable, stack);
  // Keep --default-font-family aligned so Tailwind preflight resolves to the
  // right family (otherwise medium/bold weights synthesize from a fallback).
  el.style.setProperty('--default-font-family', stack);
  // Keep --font-gotham-greek pointing at the active font even if the client's
  // chosen font has a different cssVariable, since some components reference
  // it directly via the Tailwind config.
  if (font.cssVariable !== '--font-gotham-greek') {
    el.style.setProperty('--font-gotham-greek', stack);
  }
}

/** Loading placeholder shown briefly while we fetch the client config. */
function LoadingPlaceholder() {
  return (
    <div className="p-8 text-sm text-gray-500" style={{ fontFamily: 'system-ui, sans-serif' }}>
      Loading search…
    </div>
  );
}

/** Error placeholder if config fetch fails. Helps diagnose integration issues. */
function ErrorPlaceholder({ message }: { message: string }) {
  return (
    <div
      className="p-6 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50"
      style={{ fontFamily: 'system-ui, sans-serif' }}
    >
      <strong>Search widget failed to load.</strong>
      <div className="mt-1 text-xs text-red-500">{message}</div>
    </div>
  );
}

async function fetchClientConfig(boot: BootConfig): Promise<SerializedClientConfig> {
  const url = `${boot.apiOrigin}/api/client-config?client=${encodeURIComponent(boot.storeId)}`;
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`Config fetch returned ${res.status}`);
  }
  return res.json() as Promise<SerializedClientConfig>;
}

/**
 * Generate a UUID v4. Prefers `crypto.randomUUID()` (secure contexts), and
 * falls back to a Math.random-based v4 for non-secure contexts (e.g. plain
 * http embeds) where `crypto.randomUUID` is undefined.
 */
function generateUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the Math.random fallback
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Stable per-visitor id, persisted in localStorage across sessions and tabs.
 * Wrapped in try/catch — localStorage throws in private-mode / blocked-storage
 * contexts; we degrade to a fresh (non-persisted) id rather than crash boot.
 */
function getOrCreateVisitorId(): string {
  try {
    const existing = localStorage.getItem('aca_visitor_id');
    if (existing) return existing;
    const id = generateUuid();
    localStorage.setItem('aca_visitor_id', id);
    return id;
  } catch {
    return generateUuid();
  }
}

/**
 * Per-tab session id, persisted in sessionStorage (cleared when the tab closes).
 * DISTINCT from the visitor id. Same private-mode safety as getOrCreateVisitorId.
 */
function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem('aca_session_id');
    if (existing) return existing;
    const id = generateUuid();
    sessionStorage.setItem('aca_session_id', id);
    return id;
  } catch {
    return generateUuid();
  }
}

/**
 * The logged-in host customer id, if the host exposes it — used ONLY for B2B
 * clients to attribute analytics to the wholesale ACCOUNT (not just an anonymous
 * visitor). The host sets `data-customer-id` on the mount container (or the embed
 * script tag); we never invent it. Absent → anonymous attribution as before.
 */
function readCustomerId(container: HTMLElement | null): string | undefined {
  const raw = container?.getAttribute('data-customer-id') ?? ownScriptTag?.getAttribute('data-customer-id');
  const v = raw?.trim();
  return v && v.length > 0 ? v.slice(0, 200) : undefined;
}

/**
 * The warehouse the host's logged-in customer is attached to (`data-warehouse`),
 * read from the mount container or the embed script tag exactly like
 * `data-customer-id`. Scopes search results to what that warehouse carries.
 *
 * Passed through verbatim — the SERVER normalises it and validates it against the
 * client's known codes, and ignores anything it doesn't recognise (showing the
 * full catalogue). Deliberately not validated here: the browser is the wrong
 * place to decide what a valid warehouse is, and a host typo must never be able
 * to empty someone's search results. See lib/warehouseScope.ts.
 */
function readWarehouseCode(container: HTMLElement | null): string | undefined {
  const raw = container?.getAttribute('data-warehouse') ?? ownScriptTag?.getAttribute('data-warehouse');
  const v = raw?.trim();
  return v && v.length > 0 ? v.slice(0, 32) : undefined;
}

/** Main boot sequence. Idempotent — bails cleanly if mount point is missing
 *  or shadow root is already attached. */
async function boot() {
  const bootConfig = readBootConfig(ownScriptTag);

  // Inject the default font's @font-face rules immediately so the loading
  // placeholder renders in Gotham Greek even before the API config arrives.
  // Will be replaced if the client picked a different font.
  injectGlobalFontFaces(bootConfig.apiOrigin, DEFAULT_FONT);

  const container = document.getElementById('ai-search-container');
  if (!container) {
    console.error(
      '[search] Mount point #ai-search-container not found. ' +
        'Did you forget the <div id="ai-search-container"></div> element?',
    );
    return;
  }
  if (container.shadowRoot) {
    console.warn('[search] Widget already mounted on this container — skipping.');
    return;
  }

  // Create the shadow root with placeholder branding. We update brand colors
  // again once the real config arrives.
  const shadow = container.attachShadow({ mode: 'open' });
  const styleEl = document.createElement('style');
  // Rewrite font URL placeholders to point at our domain, so @font-face
  // requests resolve correctly even when the widget is embedded cross-origin.
  styleEl.textContent = widgetCss.replace(/__WIDGET_ORIGIN__/g, bootConfig.apiOrigin);
  shadow.appendChild(styleEl);
  const inner = document.createElement('div');
  inner.id = 'aca-search-root';
  shadow.appendChild(inner);

  const root = createRoot(inner);
  root.render(<LoadingPlaceholder />);

  let config: SerializedClientConfig;
  try {
    config = await fetchClientConfig(bootConfig);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown error';
    console.error('[search] Failed to fetch client config:', message);
    root.render(<ErrorPlaceholder message={message} />);
    return;
  }

  applyBrandColors(inner, config);

  // Apply the resolved font from the API. If the client picked the default
  // (gotham-greek), this is a no-op against the rules we already injected.
  // If they picked a different font, this swaps them in.
  const resolvedFont = config.ui.font ?? DEFAULT_FONT;
  injectGlobalFontFaces(bootConfig.apiOrigin, resolvedFont);
  applyFontVariables(inner, resolvedFont);

  // Inject apiOrigin into the config so React components prefix all API fetches
  // with our backend URL — the host page's domain (e.g. aca.beta4u.gr) does
  // not have our routes, so relative fetches would 404.
  // Analytics identity: a stable per-visitor id (localStorage) and a per-tab
  // session id (sessionStorage). Threaded into the config so SearchWidget can
  // send them on /api/search requests. Both are storage-safe (see helpers).
  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();
  // B2B account attribution: ONLY for clients with external pricing (the B2B
  // marker) do we read the host-exposed logged-in customer id. B2C never sends it.
  const externalCustomerId = config.features?.externalPricing ? readCustomerId(container) : undefined;
  // Warehouse scoping is server-configured, so the widget always forwards the
  // attribute when present and lets the server decide whether it means anything.
  const warehouseCode = readWarehouseCode(container);
  const configWithOrigin: SerializedClientConfig = {
    ...config,
    apiOrigin: bootConfig.apiOrigin,
    visitorId,
    sessionId,
    ...(externalCustomerId ? { externalCustomerId } : {}),
    ...(warehouseCode ? { warehouseCode } : {}),
  };

  root.render(
    <ClientProvider serializedConfig={configWithOrigin}>
      <SearchWidget />
    </ClientProvider>,
  );

  console.log(`[search] Widget mounted for client="${config.clientId}" font="${resolvedFont.key}"`);
}

// Boot AT MOST ONCE per page. Some host integrations execute widget.js more than
// once — e.g. a SPA that re-renders the header containing our <script>, or an
// interval/router that re-injects it. Without this guard, EVERY execution re-runs
// boot() → re-fetches /api/client-config AND re-mounts SearchWidget (whose mount
// effect fires the home "popular products" search, query=''). That is a runaway
// request flood: it happened live on syfakb2b.gr (~1 re-boot/sec across many
// visitors), which drained the rate-limit store. A window-level flag survives
// re-execution and makes boot idempotent no matter what the host does. (The proper
// host-side fix is to inject the script exactly once; we must be resilient anyway.)
const __aiSearch = window as unknown as { __aiSearchBooted?: boolean };
if (__aiSearch.__aiSearchBooted) {
  console.warn(
    '[search] widget.js executed again on this page — boot already ran; skipping to prevent a re-mount loop.',
  );
} else {
  __aiSearch.__aiSearchBooted = true;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void boot();
    });
  } else {
    void boot();
  }
}
